import { useCallback, useEffect, useRef, useState } from 'react';
import { getActiveGiftsCount } from '../data/api/gifts/GiftsRoutes';
import {
    getNotifications,
    getUnreadCount,
    markAllNotificationsRead,
    markNotificationRead,
} from '../data/api/notifications/NotificationRoutes';
import useIndex from './useIndex';

/**
 * Hook compartido para el feed de notificaciones del usuario.
 *
 * - El `unread_count` se hace polling cada `pollIntervalMs` (default 12s,
 *   sweet spot entre responsive y costo backend) con un endpoint barato;
 *   el feed completo solo se carga cuando el panel se abre (vía
 *   `loadFeed`) o el caller lo pide explícitamente.
 * - El intervalo se pausa cuando la pestaña está oculta para no
 *   gastar batería ni dispararle queries inútiles al backend.
 * - La primera carga se hace inmediatamente al tener token, sin esperar
 *   un tick para mostrar el badge.
 *
 * Señal de cambio: `lastNewNotificationAt` se actualiza cada vez que
 * detectamos que `unreadCount` SUBIÓ entre dos polls consecutivos. Las
 * páginas que necesitan auto-refrescar su data cuando llega un evento
 * (ej. PrizePayoutsPage cuando se firma un pago) se suscriben a este
 * timestamp con un useEffect — es el equivalente "polling-friendly" de
 * un evento `onNotification`. Solo importa el timestamp, no su valor;
 * cualquier cambio dispara el efecto.
 */
const useNotifications = ({ pollIntervalMs = 12000 } = {}) => {
    const { token } = useIndex();
    const [items, setItems] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [pendingGiftsCount, setPendingGiftsCount] = useState(0);
    const [lastNewNotificationAt, setLastNewNotificationAt] = useState(null);
    const [loading, setLoading] = useState(false);
    const [meta, setMeta] = useState(null);
    const pollRef = useRef(null);
    // Guardamos el último valor visto para detectar SUBIDAS dentro del
    // setInterval sin que el callback dependa del state (que volvería
    // stale de cara al cierre). El ref se actualiza junto con el state.
    const prevUnreadRef = useRef(0);

    const refreshUnreadCount = useCallback(async () => {
        if (!token) return;
        try {
            const { data } = await getUnreadCount(token);
            if (data?.success) {
                const next = Number(data.unread_count ?? 0);
                // Detectar "llegó algo nuevo": el contador subió desde
                // el último poll. No nos importa cuánto, solo señalar.
                if (next > prevUnreadRef.current) {
                    setLastNewNotificationAt(Date.now());
                }
                prevUnreadRef.current = next;
                setUnreadCount(next);
            }
        } catch {
            // Silencioso: el polling no debe gritar al usuario por
            // fallos transitorios. Si el token caduca, el interceptor
            // global de axios maneja el 401.
        }
    }, [token]);

    const refreshPendingGiftsCount = useCallback(async () => {
        if (!token) return;
        try {
            const { data } = await getActiveGiftsCount(token);
            if (data?.success) setPendingGiftsCount(Number(data.count ?? 0));
        } catch {
            // Silencioso (mismo motivo que arriba).
        }
    }, [token]);

    // Para el polling y los refresh "todo a la vez". Más legible que
    // estar llamando los dos por separado en cada call site.
    const refreshIndicators = useCallback(() => {
        refreshUnreadCount();
        refreshPendingGiftsCount();
    }, [refreshUnreadCount, refreshPendingGiftsCount]);

    const loadFeed = useCallback(async ({ unreadOnly = false, page = 1, perPage = 20 } = {}) => {
        if (!token) return;
        setLoading(true);
        try {
            const params = { per_page: perPage, page };
            if (unreadOnly) params.unread = 1;
            const { data } = await getNotifications(token, params);
            if (data?.success) {
                setItems(data?.data ?? []);
                setMeta(data?.meta ?? null);
                // Aprovechamos la apertura del panel para resincronizar
                // ambos indicadores sin esperar al próximo tick del poll.
                refreshIndicators();
            }
        } finally {
            setLoading(false);
        }
    }, [token, refreshIndicators]);

    const markOneRead = useCallback(async (id) => {
        if (!token || !id) return;
        // Optimistic: actualizamos local primero para que el UI sea
        // instantáneo. Si el backend falla, refrescamos.
        setItems((prev) => prev.map((n) =>
            n.id === id ? { ...n, read_at: new Date().toISOString() } : n
        ));
        setUnreadCount((c) => {
            const next = Math.max(0, c - 1);
            // Mantener el ref sincronizado: si bajamos local pero el
            // próximo poll ve el valor real (igual o menor), no debe
            // dispararse la señal de "nuevo" por accidente.
            prevUnreadRef.current = next;
            return next;
        });
        try {
            await markNotificationRead(token, id);
        } catch {
            refreshUnreadCount();
        }
    }, [token, refreshUnreadCount]);

    const markAllRead = useCallback(async () => {
        if (!token) return;
        const now = new Date().toISOString();
        setItems((prev) => prev.map((n) => n.read_at ? n : { ...n, read_at: now }));
        setUnreadCount(0);
        prevUnreadRef.current = 0;
        try {
            await markAllNotificationsRead(token);
        } catch {
            refreshUnreadCount();
        }
    }, [token, refreshUnreadCount]);

    /* Polling de los indicadores (notif. no leídas + regalos pendientes),
       con pausa en visibility hidden. */
    useEffect(() => {
        if (!token) return undefined;

        // Primera carga inmediata.
        refreshIndicators();

        const start = () => {
            if (pollRef.current) return;
            pollRef.current = setInterval(refreshIndicators, pollIntervalMs);
        };
        const stop = () => {
            if (!pollRef.current) return;
            clearInterval(pollRef.current);
            pollRef.current = null;
        };

        const handleVisibility = () => {
            if (document.visibilityState === 'hidden') stop();
            else { refreshIndicators(); start(); }
        };

        start();
        document.addEventListener('visibilitychange', handleVisibility);

        return () => {
            stop();
            document.removeEventListener('visibilitychange', handleVisibility);
        };
    }, [token, pollIntervalMs, refreshIndicators]);

    return {
        items,
        unreadCount,
        pendingGiftsCount,
        lastNewNotificationAt,
        loading,
        meta,
        loadFeed,
        refreshUnreadCount,
        refreshPendingGiftsCount,
        markOneRead,
        markAllRead,
    };
};

export default useNotifications;
